import React from "react";
import { useHistory } from "react-router-dom";
import { OverlaySpinner } from "../../../components/OverlaySpinner";
import { SetupFlow } from "../../../components/SetupFlow/SetupFlow";
import { useWSMutation } from "../../../query/helpers";
import { useUserId } from "../../../query/hooks/helpers";
import { useFeatureFlags } from "../../../query/hooks/useFeatureFlags";
import { useMemberClientsQuery } from "../../../query/payments/queries";
import { QUERY_USERS_ACTIVITIES } from "../../../query/users/keys";
import { useMemberProfile } from "../../../query/users/queries";
import { getIsDomesticMember } from "../../../query/users/selectors";
import { WSQueries } from "../../../query/WSQuery";
import { usersService } from "../../../services/users";
import { useSetWSStore } from "../../../store";
import { useWSAnalytics } from "../../../utils/WSAnalytics";
import { PaymentsSetupLayoutLoading } from "../../components/PaymentsSetupLayout/PaymentsSetupLayout";
import {
  getHasEligibilityRequirements,
  useIsOnlyOneBusinessOwner
} from "../../utils/setUp";
import { SetupBusiness } from "./SetupBusiness";
import { SetupConsent } from "./SetupConsent";
import { SetupConsentInternational } from "./SetupConsentInternational";
import { SetupElectronic1099Consent } from "./SetupElectronic1099Consent";
import { SetupEligibilityRequirements } from "./SetupEligibilityRequirements";
import { SetupPayouts } from "./SetupPayouts";
import { SetupPayoutsConnectBank } from "./SetupPayoutsConnectBank";
import { SetupPayoutsConnectBankManually } from "./SetupPayoutsConnectBankManually";
import { SetupPayoutsInstant } from "./SetupPayoutsInstant";
import { SetupPayoutsInstantRTP } from "./SetupPayoutsInstantRTP";
import { SetupPayoutsInternational } from "./SetupPayoutsInternational";
import { SetupPersonal } from "./SetupPersonal";

type InvoicesSetupProps = {
  basePath: string;
  onBack: () => Promise<void> | void;
  onFinish: () => Promise<void> | void;
};

export const PaymentsSetup: React.FC<InvoicesSetupProps> = ({
  basePath,
  onBack,
  onFinish
}) => {
  const history = useHistory();
  const userId = useUserId();
  const memberQuery = useMemberProfile(userId);
  const memberClientsQuery = useMemberClientsQuery();
  const [
    _isOnlyOneBusinessOwner,
    setIsOnlyOneBusinessOwner
  ] = useIsOnlyOneBusinessOwner();
  const setStore = useSetWSStore();
  const { track } = useWSAnalytics();
  const featureFlagsQuery = useFeatureFlags();

  const [finishOnboarding, finishOnboardingMeta] = useWSMutation(
    async () => {
      setIsOnlyOneBusinessOwner(null);

      setStore({ productActivationAfterOnboarding: false });

      await usersService.activity.update(userId, {
        flows: {
          paymentsSetup: {
            version: 3,
            currentStep: 5,
            totalSteps: 5,
            complete: true
          }
        }
      });

      track.other("Product activation");

      await onFinish();
    },
    {
      dependencies: [QUERY_USERS_ACTIVITIES]
    }
  );

  return (
    <WSQueries
      queries={{ memberQuery, memberClientsQuery }}
      renderLoader={() => <PaymentsSetupLayoutLoading />}
    >
      {({
        memberQuery: { data: member },
        memberClientsQuery: { data: memberClients }
      }) => {
        const isDomesticMember = getIsDomesticMember(member);
        const hasEligibilityRequirements = getHasEligibilityRequirements(
          memberClients
        );

        return (
          <>
            {finishOnboardingMeta.isLoading && <OverlaySpinner />}

            <SetupFlow
              basePath={basePath}
              routes={[
                {
                  path: "/business",
                  component: SetupBusiness,
                  onBack: onBack,
                  onNext: () => {
                    history.push(basePath + "/personal");
                  }
                },
                {
                  path: "/personal",
                  component: SetupPersonal,
                  onBack: () => {
                    history.push(basePath + "/business");
                  },
                  onNext: context => {
                    if (context?.isUSPerson || isDomesticMember) {
                      history.push(basePath + "/consent");
                    } else {
                      history.push(basePath + "/consent-international");
                    }
                  }
                },
                {
                  path: "/consent",
                  component: SetupConsent,
                  onBack: () => {
                    history.push(basePath + "/personal");
                  },
                  onNext: () => {
                    history.push(basePath + "/electronic-1099-consent");
                  }
                },
                {
                  path: "/consent-international",
                  component: SetupConsentInternational,
                  onBack: () => {
                    history.push(basePath + "/personal");
                  },
                  onNext: () => {
                    if (hasEligibilityRequirements) {
                      history.push(basePath + "/eligibility-requirements");
                    } else {
                      history.push(basePath + "/payouts-international");
                    }
                  }
                },
                {
                  path: "/electronic-1099-consent",
                  component: SetupElectronic1099Consent,
                  onBack: () => {
                    history.push(basePath + "/consent");
                  },
                  onNext: () => {
                    if (hasEligibilityRequirements) {
                      history.push(basePath + "/eligibility-requirements");
                    } else {
                      history.push(basePath + "/payouts");
                    }
                  }
                },
                {
                  path: "/eligibility-requirements",
                  component: SetupEligibilityRequirements,
                  onBack: () => {
                    if (isDomesticMember) {
                      history.push(basePath + "/electronic-1099-consent");
                    } else {
                      history.push(basePath + "/consent-international");
                    }
                  },
                  onNext: () => {
                    if (isDomesticMember) {
                      history.push(basePath + "/payouts");
                    } else {
                      history.push(basePath + "/payouts-international");
                    }
                  }
                },
                {
                  path: "/payouts",
                  component: SetupPayouts,
                  onBack: () => {
                    if (hasEligibilityRequirements) {
                      history.push(basePath + "/eligibility-requirements");
                    } else {
                      history.push(basePath + "/electronic-1099-consent");
                    }
                  },
                  onNext: context => {
                    if (context?.depositType === "instantDeposit") {
                      history.push(basePath + "/payouts-instant");
                    } else if (context?.depositType === "ach") {
                      history.push(basePath + "/payouts-connect-bank");
                    } else {
                      finishOnboarding();
                    }
                  }
                },
                {
                  path: "/payouts-instant",
                  component: featureFlagsQuery.data?.rtpAccounts
                    ? SetupPayoutsInstantRTP
                    : SetupPayoutsInstant,
                  onBack: () => {
                    history.push(basePath + "/payouts");
                  },
                  onNext: () => {
                    finishOnboarding();
                  }
                },
                {
                  path: "/payouts-connect-bank",
                  component: SetupPayoutsConnectBank,
                  onBack: () => {
                    history.push(basePath + "/payouts");
                  },
                  onNext: context => {
                    if (context?.connectType === "manually") {
                      history.push(basePath + "/payouts-connect-bank-manually");
                    } else {
                      finishOnboarding();
                    }
                  }
                },
                {
                  path: "/payouts-connect-bank-manually",
                  component: SetupPayoutsConnectBankManually,
                  onBack: () => {
                    history.push(basePath + "/payouts-connect-bank");
                  },
                  onNext: () => {
                    finishOnboarding();
                  }
                },
                {
                  path: "/payouts-international",
                  component: SetupPayoutsInternational,
                  onBack: () => {
                    if (hasEligibilityRequirements) {
                      history.push(basePath + "/eligibility-requirements");
                    } else {
                      history.push(basePath + "/consent-international");
                    }
                  },
                  onNext: () => {
                    finishOnboarding();
                  }
                }
              ]}
            />
          </>
        );
      }}
    </WSQueries>
  );
};
